// 🔒 MULTI-BRANCH-FASE2-2026-05 — Contexto global de sucursal.
//
// Provee:
//   - multiBranchEnabled  (boolean global)
//   - branches            (sucursales activas que el usuario puede usar)
//   - activeBranchId      ("__all__" o un branch.id)
//   - canUseAggregate     (admin/propietario)
//   - setActiveBranchId(id)
//   - getBranchById(id)
//
// Persistencia: localStorage["active_branch_id"] + localStorage["multi_branch_enabled"].
// El segundo lo usa el axios interceptor global de lib/api.js.
//
// Reglas de acceso (espejo del backend en utils/branch_context.py):
//   - usuario con branch_ids = [a,b] → solo a y b
//   - usuario sin branch_ids y admin/propietario → todas + "Todas"
//   - usuario sin branch_ids y operativo → solo principal
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGGREGATE_KEY = '__all__';

const BranchContext = createContext(null);

export function useBranches() {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    return {
      multiBranchEnabled: false,
      branches: [],
      activeBranchId: null,
      canUseAggregate: false,
      isAggregate: false,
      setActiveBranchId: () => {},
      mainBranch: null,
      reload: () => {},
    };
  }
  return ctx;
}

const hdrs = () => ({
  Authorization: `Bearer ${localStorage.getItem('pos_token') || localStorage.getItem('token') || ''}`,
});

function isAdminUser(user) {
  if (!user) return false;
  if (user.is_super_admin) return true;
  if ((user.role || '').toLowerCase() === 'admin') return true;
  if ((user.role || '').toLowerCase() === 'propietario') return true;
  if (Number(user.role_level || 0) >= 100) return true;
  return false;
}

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [multiBranchEnabled, setEnabled] = useState(false);
  const [allBranches, setAllBranches] = useState([]);
  const [activeBranchId, setActiveBranchIdRaw] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Lista de sucursales que ESTE usuario tiene permitido ver/usar.
  const allowedBranches = useMemo(() => {
    if (!user) return [];
    const userBranchIds = Array.isArray(user.branch_ids) ? user.branch_ids : [];
    const active = allBranches.filter(b => b.is_active);
    if (userBranchIds.length > 0) {
      return active.filter(b => userBranchIds.includes(b.id));
    }
    if (isAdminUser(user)) return active;
    // Operativo sin asignación → solo principal
    const main = active.find(b => b.is_main);
    return main ? [main] : active.slice(0, 1);
  }, [allBranches, user]);

  const mainBranch = useMemo(
    () => allBranches.find(b => b.is_main && b.is_active) || null,
    [allBranches]
  );

  const canUseAggregate = useMemo(() => isAdminUser(user), [user]);

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      const s = await axios.get(`${API}/branches/_config/state`, { headers: hdrs() });
      const enabled = Boolean(s.data?.multi_branch_enabled);
      setEnabled(enabled);
      localStorage.setItem('multi_branch_enabled', enabled ? 'true' : 'false');

      if (!enabled) {
        setAllBranches([]);
        setActiveBranchIdRaw(null);
        try { localStorage.removeItem('active_branch_id'); } catch {}
        setLoaded(true);
        return;
      }

      const b = await axios.get(`${API}/branches?include_inactive=false`, { headers: hdrs() });
      const list = Array.isArray(b.data) ? b.data : [];
      setAllBranches(list);
    } catch {
      // fail-silent: en error dejamos el modo single para no romper el POS
      setEnabled(false);
      setAllBranches([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  // 🔒 MULTI-BRANCH-FASE2 — Escuchar errores de sucursal globales
  useEffect(() => {
    const handler = (ev) => {
      const { code, message } = ev?.detail || {};
      toast.error(message || 'Selecciona una sucursal antes de operar.', {
        id: `branch-${code}`,
      });
    };
    window.addEventListener('branch-error', handler);
    return () => window.removeEventListener('branch-error', handler);
  }, []);

  // Resolver activeBranchId cuando cambian las sucursales permitidas
  useEffect(() => {
    if (!multiBranchEnabled || !loaded) return;
    let stored = null;
    try { stored = localStorage.getItem('active_branch_id'); } catch {}
    const allowedIds = allowedBranches.map(b => b.id);
    const adminAggregateOK = canUseAggregate;

    // Aggregate?
    if (stored === AGGREGATE_KEY && adminAggregateOK) {
      setActiveBranchIdRaw(AGGREGATE_KEY);
      return;
    }
    // Stored & válido?
    if (stored && allowedIds.includes(stored)) {
      setActiveBranchIdRaw(stored);
      return;
    }
    // Fallback: principal o primera permitida
    const fallback = (mainBranch && allowedIds.includes(mainBranch.id))
      ? mainBranch.id
      : (allowedBranches[0]?.id || null);
    if (fallback) {
      setActiveBranchIdRaw(fallback);
      try { localStorage.setItem('active_branch_id', fallback); } catch {}
    }
  }, [multiBranchEnabled, allowedBranches, mainBranch, canUseAggregate, loaded]);

  const setActiveBranchId = useCallback((id) => {
    if (id === AGGREGATE_KEY && !canUseAggregate) return;
    setActiveBranchIdRaw(id);
    try { localStorage.setItem('active_branch_id', id); } catch {}
  }, [canUseAggregate]);

  const getBranchById = useCallback(
    (id) => allBranches.find(b => b.id === id) || null,
    [allBranches]
  );

  const value = {
    multiBranchEnabled,
    branches: allowedBranches,
    activeBranchId,
    isAggregate: activeBranchId === AGGREGATE_KEY,
    canUseAggregate,
    setActiveBranchId,
    mainBranch,
    getBranchById,
    reload,
    aggregateKey: AGGREGATE_KEY,
    loaded,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}
